import { useEffect, useRef } from 'react';

const SLIDER_MIN = 0;
const SLIDER_MAX = 10;
const SLIDER_STEP = 0.01;
const SLIDER_CLASS = 'raw-threshold-slider';
const READOUT_CLASS = 'raw-threshold-readout';
const STYLE_ELEMENT_ID = 'raw-threshold-slider-style';

const THUMB_WIDTH = 22;
const THUMB_HEIGHT = 16;
const SLIDER_WIDTH = 24;
const READOUT_WIDTH = 44;
const READOUT_HEIGHT = 18;

const CSS = `
.${SLIDER_CLASS} {
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    outline: none;
    margin: 0;
    padding: 0;
    writing-mode: vertical-lr;
    direction: rtl;
}
.${SLIDER_CLASS}::-webkit-slider-runnable-track {
    background: transparent;
    border: none;
    width: 100%;
}
.${SLIDER_CLASS}::-moz-range-track {
    background: transparent;
    border: none;
    width: 100%;
}
.${SLIDER_CLASS}::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: ${THUMB_WIDTH}px;
    height: ${THUMB_HEIGHT}px;
    border-radius: 4px;
    background: white;
    border: 2px solid #222;
    box-shadow: 0 0 3px rgba(0, 0, 0, 0.6);
    cursor: ns-resize;
}
.${SLIDER_CLASS}::-moz-range-thumb {
    width: ${THUMB_WIDTH}px;
    height: ${THUMB_HEIGHT}px;
    border-radius: 4px;
    background: white;
    border: 2px solid #222;
    box-shadow: 0 0 3px rgba(0, 0, 0, 0.6);
    cursor: ns-resize;
}
.${READOUT_CLASS} {
    position: absolute;
    width: ${READOUT_WIDTH}px;
    height: ${READOUT_HEIGHT}px;
    line-height: ${READOUT_HEIGHT}px;
    text-align: center;
    font-family: monospace;
    font-size: 12px;
    color: white;
    background: rgba(0, 0, 0, 0.65);
    border-radius: 3px;
    pointer-events: none;
    user-select: none;
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.6);
    z-index: 5;
}
`;

function ensureStyleInjected() {
    if (document.getElementById(STYLE_ELEMENT_ID)) return;
    const styleEl = document.createElement('style');
    styleEl.id = STYLE_ELEMENT_ID;
    styleEl.textContent = CSS;
    document.head.appendChild(styleEl);
}

function computeBarPixels(actor, { width, height }) {
    const barPos = actor?.getBarPosition?.();
    const barSize = actor?.getBarSize?.();
    if (!barPos || !barSize || barSize[1] < 1.0) {
        const fallbackHeight = height * 0.60;
        const top = (height - fallbackHeight) / 2;
        const bottom = top + fallbackHeight;
        const centerX = width - 60;
        return [top, bottom, centerX];
    }
    const segSpace = 0.02;
    const tickBottomNorm = barPos[1] + segSpace * barSize[1];
    const tickTopNorm = barPos[1] + (1 - segSpace) * barSize[1];
    const pixelTop = height - ((tickTopNorm + 1) / 2) * height;
    const pixelBottom = height - ((tickBottomNorm + 1) / 2) * height;
    const barMidXNorm = barPos[0] + 0.5 * barSize[0];
    const centerX = ((barMidXNorm + 1) / 2) * width;
    return [pixelTop, pixelBottom, centerX];
}

function placeReadout(actor, slider, readout, dims) {
    const [pixelTop, pixelBottom, centerX] = computeBarPixels(actor, dims);
    const thumbCenterY = pixelTop + (1 - (parseFloat(slider.value) - SLIDER_MIN) / (SLIDER_MAX - SLIDER_MIN)) * (pixelBottom - pixelTop);
    readout.style.left = `${centerX - SLIDER_WIDTH / 2 - READOUT_WIDTH - 6}px`;
    readout.style.top = `${thumbCenterY - READOUT_HEIGHT / 2}px`;
}

/**
 * Vertical threshold slider rendered as a thumb-only handle that sits over the right-side
 * vtk scalar bar (default vertical layout). The slider's track is hidden via CSS — the bar
 * itself is the visual track. The thumb's center is aligned with the bar's value-0 (bottom)
 * and value-10 (top) positions by reading the scalar-bar actor's boxPosition/boxSize. A small
 * numeric readout floats to the left of the thumb and follows it vertically.
 *
 * @param containerRef
 * @param {boolean} initialized
 * @param {boolean} isVisible
 * @param {number} value
 * @param {(value: number) => void} onChange
 * @param scalarBarActor vtkScalarBarActor whose boxPosition/boxSize anchors the slider.
 */
export function useRawThresholdSlider(containerRef, initialized, isVisible, value, onChange, scalarBarActor) {
    const context = useRef(null);
    const container = containerRef.current;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const scalarBarActorRef = useRef(scalarBarActor);
    scalarBarActorRef.current = scalarBarActor;

    const positionElements = (slider, readout, dims) => {
        const [pixelTop, pixelBottom, centerX] = computeBarPixels(scalarBarActorRef.current, dims);
        const sliderHeight = pixelBottom - pixelTop + THUMB_HEIGHT;
        slider.style.left = `${centerX - SLIDER_WIDTH / 2}px`;
        slider.style.top = `${pixelTop - THUMB_HEIGHT / 2}px`;
        slider.style.width = `${SLIDER_WIDTH}px`;
        slider.style.height = `${sliderHeight}px`;
        placeReadout(scalarBarActorRef.current, slider, readout, dims);
    };

    useEffect(() => {
        if (container && !context.current) {
            ensureStyleInjected();

            const slider = document.createElement('input');
            slider.type = 'range';
            slider.min = SLIDER_MIN;
            slider.max = SLIDER_MAX;
            slider.step = SLIDER_STEP;
            slider.value = value;
            slider.classList.add(SLIDER_CLASS);
            slider.setAttribute('orient', 'vertical');
            slider.style.position = 'absolute';
            slider.style.zIndex = '5';
            slider.style.pointerEvents = 'auto';

            const readout = document.createElement('div');
            readout.classList.add(READOUT_CLASS);
            readout.textContent = Number(value).toFixed(2);

            slider.addEventListener('input', (event) => {
                const next = parseFloat(event.target.value);
                readout.textContent = next.toFixed(2);
                placeReadout(scalarBarActorRef.current, slider, readout, container.getBoundingClientRect());
                onChangeRef.current?.(next);
            });

            const swallow = (event) => event.stopPropagation();
            for (const eventName of ['pointerdown', 'pointerup', 'pointermove', 'mousedown', 'mouseup', 'mousemove', 'click', 'wheel', 'touchstart', 'touchmove', 'touchend']) {
                slider.addEventListener(eventName, swallow);
            }

            container.appendChild(slider);
            container.appendChild(readout);
            positionElements(slider, readout, container.getBoundingClientRect());

            const reposition = () => {
                if (container && context.current) {
                    positionElements(context.current.slider, context.current.readout, container.getBoundingClientRect());
                }
            };
            const resizeObserver = new ResizeObserver(reposition);
            resizeObserver.observe(container);

            context.current = { slider, readout, resizeObserver };
        }
        let ctx = context.current;

        return () => {
            if (ctx) {
                ctx.resizeObserver.unobserve(container);
                if (ctx.slider.parentNode === container) container.removeChild(ctx.slider);
                if (ctx.readout.parentNode === container) container.removeChild(ctx.readout);
                context.current = null;
                ctx = null;
            }
        };
    }, [container]);

    useEffect(() => {
        if (context.current) {
            const visibility = initialized && isVisible ? 'inherit' : 'hidden';
            context.current.slider.style.visibility = visibility;
            context.current.readout.style.visibility = visibility;
        }
    }, [isVisible, initialized, container]);

    useEffect(() => {
        if (!context.current) return;
        const { slider, readout } = context.current;
        if (Math.abs(parseFloat(slider.value) - value) > 1e-6) {
            slider.value = value;
            readout.textContent = Number(value).toFixed(2);
            if (container) {
                placeReadout(scalarBarActorRef.current, slider, readout, container.getBoundingClientRect());
            }
        }
    }, [value]);

    // Re-position when the scalar bar actor becomes available or changes. One rAF defers the read
    // until after vtk has had a chance to populate the actor's geometry.
    useEffect(() => {
        if (!context.current || !container) return;
        const rafId = requestAnimationFrame(() => {
            if (context.current && container) {
                positionElements(context.current.slider, context.current.readout, container.getBoundingClientRect());
            }
        });
        return () => cancelAnimationFrame(rafId);
    }, [scalarBarActor, isVisible]);
}
